import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { MarketplaceOrganization, OrgMembershipStatus } from '@/types/marketplace';
import { logger } from '@/utils/logger';
import { insertNotification } from '@/utils/notificationInsert';
import { fetchProfileDisplayName, formatOrgDisplayName } from '@/utils/notificationHelpers';

function mapStatus(s: string): OrgMembershipStatus {
  if (s === 'pending' || s === 'accepted' || s === 'rejected') return s;
  if (s === 'removed') return 'none';
  return 'none';
}

export function useMarketplaceOrganizations(volunteerUserId: string | null) {
  const [organizations, setOrganizations] = useState<MarketplaceOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!volunteerUserId) {
      setOrganizations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image, description, location, marketplace_listed')
        .eq('role', 'organization')
        .neq('id', volunteerUserId)
        .order('first_name', { ascending: true });

      if (profilesError) throw profilesError;

      const { data: membershipRows, error: memError } = await supabase
        .from('organization_memberships')
        .select('id, organization_id, status')
        .eq('volunteer_id', volunteerUserId);

      if (memError) throw memError;

      const byOrgId: Record<string, { id: string; status: OrgMembershipStatus }> = {};
      for (const row of membershipRows ?? []) {
        const r = row as { id: string; organization_id: string; status: string };
        byOrgId[r.organization_id] = {
          id: r.id,
          status: mapStatus(r.status),
        };
      }

      const list: MarketplaceOrganization[] = [];
      for (const p of profilesData ?? []) {
        const row = p as {
          id: string;
          first_name: string | null;
          last_name: string | null;
          profile_image: string | null;
          description: string | null;
          location: string | null;
          marketplace_listed?: boolean | null;
        };
        const m = byOrgId[row.id];
        const membershipStatus = m?.status ?? 'none';
        // Unlisted/private org: hide from strangers, but keep visible if this volunteer already has (or had) a membership row
        const isPrivate = row.marketplace_listed === false;
        if (
          isPrivate &&
          membershipStatus !== 'accepted' &&
          membershipStatus !== 'pending' &&
          membershipStatus !== 'rejected'
        ) {
          continue;
        }
        list.push({
          id: row.id,
          first_name: row.first_name,
          last_name: row.last_name,
          profile_image: row.profile_image,
          description: row.description,
          location: row.location,
          membershipStatus,
          membershipRowId: m?.id ?? null,
        });
      }

      setOrganizations(list);
    } catch (e) {
      logger.error('useMarketplaceOrganizations', e);
      setError(e instanceof Error ? e.message : 'Failed to load organizations');
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [volunteerUserId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const joinOrganization = useCallback(
    async (organizationId: string): Promise<{ ok: boolean; message?: string }> => {
      if (!volunteerUserId) return { ok: false, message: 'Not signed in' };
      const existing = organizations.find((o) => o.id === organizationId);
      if (existing?.membershipStatus === 'accepted' || existing?.membershipStatus === 'pending') {
        return { ok: true };
      }
      try {
        let membershipId: string | null = null;
        if (existing?.membershipStatus === 'rejected' && existing.membershipRowId) {
          const { error: upErr } = await supabase
            .from('organization_memberships')
            .update({
              status: 'pending',
              role: 'member',
            })
            .eq('id', existing.membershipRowId)
            .eq('volunteer_id', volunteerUserId);
          if (upErr) return { ok: false, message: upErr.message };
          membershipId = existing.membershipRowId;
        } else {
          const { data: row, error: insErr } = await supabase
            .from('organization_memberships')
            .insert({
              volunteer_id: volunteerUserId,
              organization_id: organizationId,
              status: 'pending',
            })
            .select('id')
            .single();
          if (insErr) return { ok: false, message: insErr.message };
          membershipId = row?.id ?? null;
        }

        if (membershipId) {
          const orgProfile = existing ?? organizations.find((o) => o.id === organizationId);
          const volunteerName = await fetchProfileDisplayName(volunteerUserId);
          const orgLabel = formatOrgDisplayName(orgProfile?.first_name, orgProfile?.last_name, 'your organization');
          insertNotification({
            user_id: organizationId,
            title: 'Membership request',
            message: `${volunteerName} requested to join ${orgLabel}.`,
            type: 'organization_membership_pending',
            data: {
              membership_id: membershipId,
              organization_id: organizationId,
              volunteer_id: volunteerUserId,
              volunteer_name: volunteerName,
              organization_name: orgLabel,
            },
            read: false,
          });
        }

        await fetchData();
        return { ok: true };
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Request failed';
        return { ok: false, message: msg };
      }
    },
    [volunteerUserId, organizations, fetchData]
  );

  return {
    organizations,
    loading,
    error,
    refetch: fetchData,
    joinOrganization,
  };
}

export function filterMarketplaceOrganizations(
  orgs: MarketplaceOrganization[],
  query: string
): MarketplaceOrganization[] {
  const q = query.trim().toLowerCase();
  if (!q) return orgs;
  return orgs.filter((o) => {
    const name = [o.first_name, o.last_name].filter(Boolean).join(' ').toLowerCase();
    const desc = (o.description ?? '').toLowerCase();
    const loc = (o.location ?? '').toLowerCase();
    return name.includes(q) || desc.includes(q) || loc.includes(q);
  });
}

export function filterMarketplaceEvents<T extends { name: string; location?: string | null; description?: string | null }>(
  events: T[],
  query: string
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return events;
  return events.filter((e) => {
    const name = e.name.toLowerCase();
    const loc = (e.location ?? '').toLowerCase();
    const desc = (e.description ?? '').toLowerCase();
    return name.includes(q) || loc.includes(q) || desc.includes(q);
  });
}
